import React, { useEffect, useState } from "react";
import { selectUser } from "../../../features/userSlice";
import { useSelector } from "react-redux";
import {
  MenuItem,
  Select,
  TextField,
  Button,
  Dialog,
  Tooltip,
} from "@mui/material";
import DatePicker from "react-datepicker";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import { IconTile } from "../../../components/UI/Page";
import {
  default_door,
  door_size,
  grille_type,
  handing_fields,
  hardware_color,
  yes_no,
} from "../../../components/Forms/SelectValues/values";
import {
  formAutoSave,
  hasDraftContent,
} from "../../../components/Forms/Misc/utilities";
import { Link, useNavigate } from "react-router-dom";

const ExteriorDoor = (props) => {
  //This will have data or be null either is fine.
  const [formData, setFormData] = useState(props.formData);
  const [formID, setFormID] = useState(null);
  const [autoSaveInterval, setASInterval] = useState(60);
  const [submitStatus, setStatus] = useState(false);
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  //Using existing door data if it exists, if not use a blank row.
  const [doors, setDoors] = useState(props.formData.doorData || default_door);

  // Auto Save Counter
  const [currentCount, setCount] = useState(1);

  useEffect(() => {
    if (currentCount <= 0) {
      return;
    }

    if (currentCount >= autoSaveInterval) {
      if (hasDraftContent(formData)) {
        formAutoSave(formData);
      }
      setCount(1);
    }

    const id = setInterval(timer, 1000);
    return () => clearInterval(id);
  }, [currentCount]);

  const timer = () => setCount(currentCount + 1);

  // Handle door info entries, update formData everytime a change is made to the door information
  const handleDoor = (e, obj, index) => {
    var array = [...doors]; // make a separate copy of the array
    array[index][`${obj}`] = e.target.value;
    setDoors(array);
  };

  const handleCopy = (index) => {
    var array = [...doors]; // make a separate copy of the array

    var newDoor = {
      door_size: array[index].door_size,
      description: array[index].description,
      quantity: array[index].quantity,
      size: array[index].size,
      handing: array[index].handing,
      inswing: array[index].inswing,
      jamb_thickness: array[index].jamb_thickness,
      deadbolt: array[index].deadbolt,
      peep_site: array[index].peep_site,
      sill_cover: array[index].sill_cover,
      outswing: array[index].outswing,
      quoted_price: array[index].quoted_price,
    };
    array.push(newDoor);
    setDoors(array);
  };

  const handleSubmit = async () => {
    //Once the form is submitted, stop the auto save.
    setASInterval(9999);
    var data = formData;

    data.entry_date = new Date();
    data.submit_user = user;
    data.form_type = formData.form_type.toUpperCase();

    submitForm(data);
  };

  const submitForm = async (data) => {
    const rawResponse = await fetch("/api/forms/submit", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const content = await rawResponse.json();

    if (content.success === 1) {
      setStatus(true);
      setFormID(content.form_id);
      return;
    } else {
      setStatus(false);
      return;
    }
  };

  useEffect(() => {
    var array = [...doors]; // make a separate copy of the array
    formData.doorData = array;
    setFormData({ ...formData });
  }, [doors]);

  //Get Valid Date for Date Picker
  const validDate = () => {
    if (formData.shipping_date) {
      //console.log(formData.shipping_date);

      return new Date(formData.shipping_date);
    } else {
      setFormData({ ...formData, shipping_date: new Date() });
      return new Date();
    }
  };

  // Shared field chrome: label above, full-width control.
  const fieldLabel = "mb-1 block text-sm font-medium";
  const selectProps = (label) => ({
    fullWidth: true,
    size: "small",
    SelectDisplayProps: { "aria-label": label },
  });
  const textProps = (label) => ({
    fullWidth: true,
    size: "small",
    variant: "outlined",
    inputProps: { "aria-label": label },
  });

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-6 text-[color:var(--app-text)] sm:px-6">
      <Dialog
        open={submitStatus}
        onClose={() => {
          setStatus(false);
          navigate("/forms/viewall");
        }}
        maxWidth="xs"
        fullWidth
      >
        <div className="p-8 text-center">
          <div className="app-icon-tile mx-auto mb-4 h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
          </div>
          <p className="text-xl font-semibold">
            {formData.form_type.toUpperCase()} Submitted Successfully
          </p>
          <p className="app-muted mt-1 text-sm">Notification has been sent!</p>
          <p className="mt-3 text-sm">
            <Link
              to={`/api/forms/pdf?id=${formID}`}
              target="_blank"
              className="app-link"
            >
              [Click to download]
            </Link>
          </p>
          <div className="mt-6 flex justify-center">
            <Button
              variant="contained"
              onClick={() => {
                setStatus(false);
                navigate("/forms/viewall");
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>

      <div className="app-card flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <IconTile icon={DoorFrontIcon} tone="sky" />
          <div className="min-w-0">
            <p className="app-eyebrow">Forms</p>
            <h1 className="text-xl font-bold tracking-tight">
              Exterior Door {formData.form_type.toUpperCase()}
            </h1>
            <p className="app-muted mt-0.5 truncate text-sm">
              {formData.customer} | Job #{formData.job}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
          Autosaving in ({autoSaveInterval - currentCount})
        </span>
      </div>

      {/* Delivery Date and Instrutions (2) */}
      <section className="app-card rounded-2xl border p-5 sm:p-6">
        <h2 className="app-eyebrow mb-4">General Information</h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className={fieldLabel}>Shipping Date</label>
            <DatePicker
              selected={validDate()}
              minDate={new Date()}
              onChange={(date) =>
                setFormData({ ...formData, shipping_date: date })
              }
              className="app-input w-full rounded-xl border px-3 py-2 text-sm"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={fieldLabel}>Special Instructions</label>
            <TextField
              {...textProps("Special Instructions")}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  instructions: e.target.value,
                });
              }}
              multiline={true}
              rows={2}
              value={formData.instructions || ""}
            />
          </div>
        </div>

        <hr className="app-divider my-5 border-t" />

        {/* Kit Information (8 Inputs) */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className={fieldLabel}>
              Will This Order Accompany a Window Order?
            </label>
            <Select
              {...selectProps("Will This Order Accompany a Window Order?")}
              value={formData.accompany_window_order || ""}
              onChange={(e) => {
                //console.log(e.target.value);
                setFormData({
                  ...formData,
                  accompany_window_order: e.target.value,
                });
              }}
            >
              {yes_no.map((item, index) => {
                return (
                  <MenuItem key={index} value={item}>
                    {item}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
          <div>
            <label className={fieldLabel}>Grille Type</label>
            <Select
              {...selectProps("Grille Type")}
              value={formData.grille_type || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  grille_type: e.target.value,
                })
              }
            >
              {grille_type.map((item, index) => {
                return (
                  <MenuItem key={index} value={item}>
                    {item}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
          <div>
            <label className={fieldLabel}>Hardware Color</label>
            <Select
              {...selectProps("Hardware Color")}
              value={formData.hardware_color || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  hardware_color: e.target.value,
                })
              }
            >
              {hardware_color.map((item, index) => {
                return (
                  <MenuItem key={index} value={item}>
                    {item}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
        </div>
      </section>

      {/* Door Configurator */}
      <section className="app-card rounded-2xl border p-5 sm:p-6">
        <h2 className="app-eyebrow mb-4">Door Information</h2>
        <div className="space-y-4">
          {doors.map((item, index) => {
            return (
              <div key={index} className="app-surface rounded-xl border p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold">Door {index + 1}</p>
                  <div className="flex items-center gap-1">
                    <Tooltip title="Add door">
                      <button
                        type="button"
                        aria-label="Add door"
                        className="app-icon-btn"
                        onClick={() => {
                          var array = [...doors]; // make a separate copy of the array
                          if (index !== -1) {
                            array.push({
                              door_type: "",
                              french: "",
                              quantity: "",
                              size: "",
                              handing: "",
                              other_information: "",
                              quoted_price: "",
                            });
                          }
                          setDoors(array);
                        }}
                      >
                        <ControlPointIcon />
                      </button>
                    </Tooltip>
                    {index !== 0 && (
                      <Tooltip title="Remove door">
                        <button
                          type="button"
                          aria-label="Remove door"
                          className="app-icon-btn hover:!text-rose-600 dark:hover:!text-rose-300"
                          onClick={() => {
                            var array = [...doors]; // make a separate copy of the array
                            if (index !== -1) {
                              array.splice(index, 1);
                              setDoors(array);
                            }
                          }}
                        >
                          <RemoveCircleOutlineIcon />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Copy door">
                      <button
                        type="button"
                        aria-label="Copy door"
                        className="app-icon-btn"
                        onClick={() => handleCopy(index)}
                      >
                        <ContentCopyIcon />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  <div>
                    <label className={fieldLabel}>QTY</label>
                    <TextField
                      {...textProps("QTY")}
                      onChange={(e) => handleDoor(e, "quantity", index)}
                      multiline={true}
                      value={doors[index].quantity || ""}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                    <label className={fieldLabel}>Description</label>
                    <TextField
                      {...textProps("Description")}
                      onChange={(e) => handleDoor(e, "description", index)}
                      multiline={true}
                      value={doors[index].description || ""}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 lg:col-span-2">
                    <label className={fieldLabel}>Door Size</label>
                    <Select
                      {...selectProps("Door Size")}
                      value={doors[index].door_size || ""}
                      onChange={(e) => handleDoor(e, "door_size", index)}
                    >
                      {door_size.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                  <div className="col-span-2 sm:col-span-1 lg:col-span-2">
                    <label className={fieldLabel}>Handing</label>
                    <Select
                      {...selectProps("Handing")}
                      value={doors[index].handing || ""}
                      onChange={(e) => handleDoor(e, "handing", index)}
                    >
                      {handing_fields.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                  <div>
                    <label className={fieldLabel}>Inswing</label>
                    <TextField
                      {...textProps("Inswing")}
                      onChange={(e) => handleDoor(e, "inswing", index)}
                      multiline={true}
                      value={doors[index].inswing || ""}
                    />
                  </div>
                  <div>
                    <label className={fieldLabel}>Outswing</label>
                    <TextField
                      {...textProps("Outswing")}
                      onChange={(e) => handleDoor(e, "outswing", index)}
                      multiline={true}
                      value={doors[index].outswing || ""}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 lg:col-span-2">
                    <label className={fieldLabel}>Jamb Thickness</label>
                    <TextField
                      {...textProps("Jamb Thickness")}
                      onChange={(e) => handleDoor(e, "jamb_thickness", index)}
                      multiline={true}
                      value={doors[index].jamb_thickness || ""}
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label className={fieldLabel}>Dead Bolt</label>
                    <Select
                      {...selectProps("Dead Bolt")}
                      value={doors[index].deadbolt || ""}
                      onChange={(e) => handleDoor(e, "deadbolt", index)}
                    >
                      {yes_no.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                  <div className="lg:col-span-2">
                    <label className={fieldLabel}>Peep Site</label>
                    <Select
                      {...selectProps("Peep Site")}
                      value={doors[index].peep_site || ""}
                      onChange={(e) => handleDoor(e, "peep_site", index)}
                    >
                      {yes_no.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                  <div className="lg:col-span-2">
                    <label className={fieldLabel}>Sill Cover</label>
                    <Select
                      {...selectProps("Sill Cover")}
                      value={doors[index].sill_cover || ""}
                      onChange={(e) => handleDoor(e, "sill_cover", index)}
                    >
                      {yes_no.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                  <div className="lg:col-span-2">
                    <label className={fieldLabel}>Quoted Price</label>
                    <TextField
                      {...textProps("Quoted Price")}
                      onChange={(e) => handleDoor(e, "quoted_price", index)}
                      value={doors[index].quoted_price || ""}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex justify-end pb-4">
        <Button onClick={handleSubmit} variant="contained" size="large">
          Submit
        </Button>
      </div>
    </div>
  );
};

export default ExteriorDoor;
